import React, { forwardRef } from 'react'

/**
 * Form field primitives. All three (<TextInput>, <TextArea>, <Select>) share
 * the same base visual style so a form reads consistently even when the
 * author mixes field types.
 *
 * Base: rounded-lg, border-border, focus ring on brand-600/30.
 * Size: px-3 py-2, text-sm — matches the dashboard's form rhythm.
 *
 * Pass `className` to append (e.g. "font-mono", "w-32"); the base classes
 * always apply.
 */

export const inputBaseClass =
  'w-full px-3 py-2 rounded-lg border border-border bg-surface text-sm text-txt-primary placeholder:text-txt-tertiary focus:outline-none focus:ring-2 focus:ring-brand-600/30 focus:border-brand-600 transition-colors'

export const TextInput = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ className = '', ...rest }, ref) => (
    <input ref={ref} type={rest.type || 'text'} className={`${inputBaseClass} ${className}`} {...rest} />
  )
)
TextInput.displayName = 'TextInput'

export const TextArea = forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(({ className = '', ...rest }, ref) => (
  <textarea ref={ref} className={`${inputBaseClass} resize-none ${className}`} {...rest} />
))
TextArea.displayName = 'TextArea'

export const Select = forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(
  ({ className = '', children, ...rest }, ref) => (
    <select ref={ref} className={`${inputBaseClass} ${className}`} {...rest}>
      {children}
    </select>
  )
)
Select.displayName = 'Select'

/**
 * Checkbox with a two-line label: bold primary text plus an optional tertiary
 * description. Matches the pattern used on the Widget Settings toggles and
 * the Roles & Permissions grid.
 *
 * Usage:
 *   <Checkbox
 *     label="Use agents' real profile photos"
 *     description="Falls back to initials if an agent hasn't uploaded an avatar"
 *     checked={...} onChange={...}
 *   />
 */
export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: string
  description?: string
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, description, className = '', ...rest }, ref) => (
    <label className={`flex items-start gap-3 cursor-pointer group ${className}`}>
      <input
        ref={ref}
        type="checkbox"
        className="mt-0.5 w-4 h-4 rounded border-border text-brand-600 focus:ring-brand-600/30"
        {...rest}
      />
      <div className="text-sm">
        <div className="font-medium text-txt-primary group-hover:text-brand-600 transition-colors">
          {label}
        </div>
        {description && <div className="text-xs text-txt-tertiary mt-0.5">{description}</div>}
      </div>
    </label>
  )
)
Checkbox.displayName = 'Checkbox'
